import React, { useState } from 'react';
import { ArrowUpTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { GasPod, CreateGasMeterReadingData } from '../../types/gas';
import { gasMeterReadingService } from '../../services/gasService';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

interface BulkGasReadingUploaderProps {
  customerId: string;
  gasPods: GasPod[];
  onUploaded: () => void;
}

interface ParsedReading {
  podId: string;
  gasDay: string;
  meterStatus?: number;
  consumptionM3?: number;
  energyKwh?: number;
  isValid: boolean;
  error?: string;
  podDbId?: string;
}

const BulkGasReadingUploader: React.FC<BulkGasReadingUploaderProps> = ({ customerId, gasPods, onUploaded }) => {
  const { t } = useLanguage();
  const [textInput, setTextInput] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [parsedReadings, setParsedReadings] = useState<ParsedReading[]>([]);
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());
  const [isUploading, setIsUploading] = useState(false);

  const parseLine = (line: string, index: number): ParsedReading => {
    const parts = line.split(';').map(p => p.trim());
    if (parts.length < 2) {
      return { podId: line, gasDay: '', isValid: false, error: t('gas.reading.bulkMessages.errors.podIdAndGasDayRequired') };
    }

    const podId = parts[0];
    const gasDay = parts[1];
    const meterStatus = parts[2] ? Number(parts[2].replace(',', '.')) : undefined;
    const consumptionM3 = parts[3] ? Number(parts[3].replace(',', '.')) : undefined;
    const energyKwh = parts[4] ? Number(parts[4].replace(',', '.')) : undefined;

    const pod = gasPods.find(p => p.podId === podId);
    if (!pod) {
      return { podId, gasDay, meterStatus, consumptionM3, energyKwh, isValid: false, error: t('gas.reading.bulkMessages.errors.unknownPodId') };
    }

    if (!gasDay.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return { podId, gasDay, meterStatus, consumptionM3, energyKwh, isValid: false, error: t('gas.reading.bulkMessages.errors.invalidGasDayFormat') };
    }

    if (meterStatus !== undefined && isNaN(meterStatus)) {
      return { podId, gasDay, meterStatus, consumptionM3, energyKwh, isValid: false, error: t('gas.reading.bulkMessages.errors.invalidMeterStatus') };
    }
    if (consumptionM3 !== undefined && isNaN(consumptionM3)) {
      return { podId, gasDay, meterStatus, consumptionM3, energyKwh, isValid: false, error: t('gas.reading.bulkMessages.errors.invalidConsumption') };
    }
    if (energyKwh !== undefined && isNaN(energyKwh)) {
      return { podId, gasDay, meterStatus, consumptionM3, energyKwh, isValid: false, error: t('gas.reading.bulkMessages.errors.invalidEnergy') };
    }

    return { podId, gasDay, meterStatus, consumptionM3, energyKwh, isValid: true, podDbId: pod.id };
  };

  const handleParse = () => {
    if (!textInput.trim()) {
      toast.error(t('gas.reading.bulkMessages.noInput'));
      return;
    }

    let lines = textInput.split('\n').filter(line => line.trim());
    if (lines.length > 0 && lines[0].toLowerCase().includes('pod')) {
      lines = lines.slice(1);
    }

    const parsed = lines.map((line, index) => parseLine(line, index + 1));
    setParsedReadings(parsed);
    setShowPreview(true);

    const validIndexes = new Set(parsed.map((r, i) => r.isValid ? i : -1).filter(i => i !== -1));
    setSelectedIndexes(validIndexes);

    const validCount = parsed.filter(r => r.isValid).length;
    const invalidCount = parsed.filter(r => !r.isValid).length;

    if (invalidCount > 0) {
      toast.error(t('gas.reading.bulkMessages.invalidRows', { count: invalidCount }));
    } else {
      toast.success(t('gas.reading.bulkMessages.parsedRows', { count: validCount }));
    }
  };

  const handleUpload = async () => {
    const toUpload = parsedReadings.filter((r, i) => r.isValid && selectedIndexes.has(i) && r.podDbId);
    if (toUpload.length === 0) {
      toast.error(t('gas.reading.bulkMessages.noSelectedRows'));
      return;
    }

    setIsUploading(true);
    try {
      for (const reading of toUpload) {
        const data: CreateGasMeterReadingData = {
          customerId,
          gasPodId: reading.podDbId!,
          gasDay: reading.gasDay,
          readingDate: reading.gasDay,
          meterStatus: reading.meterStatus,
          consumptionM3: reading.consumptionM3,
          energyKwh: reading.energyKwh,
          status: 'read',
          source: 'import'
        };
        await gasMeterReadingService.createGasMeterReading(data);
      }
      toast.success(t('gas.reading.bulkMessages.uploadedRows', { count: toUpload.length }));
      setTextInput('');
      setShowPreview(false);
      setParsedReadings([]);
      setSelectedIndexes(new Set());
      onUploaded();
    } catch (error) {
      console.error('Error uploading gas readings:', error);
      toast.error(t('gas.reading.bulkMessages.uploadError'));
    } finally {
      setIsUploading(false);
    }
  };

  const downloadExample = () => {
    const csv = 'POD_ID;GAS_DAY;METER_STATUS;CONSUMPTION_M3;ENERGY_KWH\n' +
      'HU_GAS_1234567890123456789012345678;2026-07-01;12345.67;100.50;1050.20\n' +
      'HU_GAS_1234567890123456789012345678;2026-07-02;12456.78;110.25;1155.30';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'gaz_meroallas_minta.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-green-900 flex items-center gap-2">
          <ArrowUpTrayIcon className="h-4 w-4" />
          Tömeges mérőállás feltöltés
        </h3>
        <button
          type="button"
          onClick={downloadExample}
          className="text-xs text-green-700 hover:text-green-900 underline"
        >
          Minta CSV letöltése
        </button>
      </div>

      <p className="text-xs text-gray-600">
        Formátum: <code>POD_ID;GAS_DAY;METER_STATUS;CONSUMPTION_M3;ENERGY_KWH</code>
      </p>

      <textarea
        value={textInput}
        onChange={e => setTextInput(e.target.value)}
        rows={6}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500"
        placeholder="HU_GAS_1234567890123456789012345678;2026-07-01;12345.67;100.50;1050.20"
      />

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            setShowPreview(false);
            setParsedReadings([]);
          }}
          className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
        >
          Elrejtés
        </button>
        <button
          type="button"
          onClick={handleParse}
          className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
        >
          Feldolgozás
        </button>
      </div>

      {showPreview && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-700">
              {parsedReadings.filter(r => r.isValid).length} érvényes / {parsedReadings.filter(r => !r.isValid).length} hibás
            </p>
            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedIndexes.size === parsedReadings.filter(r => r.isValid).length && parsedReadings.some(r => r.isValid)}
                onChange={e => {
                  if (e.target.checked) {
                    const allValid = new Set(parsedReadings.map((r, i) => r.isValid ? i : -1).filter(i => i !== -1));
                    setSelectedIndexes(allValid);
                  } else {
                    setSelectedIndexes(new Set());
                  }
                }}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              Mindet kijelöl
            </label>
          </div>

          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Kijelöl</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">POD ID</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Gáznap</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Mérőállás</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">m³</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">kWh</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500">Hiba</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {parsedReadings.map((reading, index) => (
                  <tr key={index} className={reading.isValid ? 'hover:bg-gray-50' : 'bg-red-50'}>
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIndexes.has(index)}
                        disabled={!reading.isValid}
                        onChange={e => {
                          const updated = new Set(selectedIndexes);
                          if (e.target.checked) updated.add(index);
                          else updated.delete(index);
                          setSelectedIndexes(updated);
                        }}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                    </td>
                    <td className="px-2 py-2 text-xs font-mono">{reading.podId}</td>
                    <td className="px-2 py-2 text-xs">{reading.gasDay}</td>
                    <td className="px-2 py-2 text-xs">{reading.meterStatus ?? '—'}</td>
                    <td className="px-2 py-2 text-xs">{reading.consumptionM3 ?? '—'}</td>
                    <td className="px-2 py-2 text-xs">{reading.energyKwh ?? '—'}</td>
                    <td className="px-2 py-2 text-xs text-red-600">{reading.error || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={handleUpload}
            disabled={isUploading || selectedIndexes.size === 0}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isUploading ? 'Feltöltés...' : `${selectedIndexes.size} mérőállás feltöltése`}
          </button>
        </div>
      )}
    </div>
  );
};

export default BulkGasReadingUploader;
